import React, { useEffect, useState, useRef } from 'react';
import { useAppSelector, useAppDispatch } from 'src/store';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import {
  fetchConversationWithMessages,
  removeConversationById,
  removeMessageFromConversation,
  toggleOpenConversation,
  selectConversationById,
  selectAllConversations
} from 'src/features/account/conversations.slice';
import {
  getContact,
  removeContactState,
  selectAllContacts,
} from 'src/features/account/contacts.slice';
import {
  fetchChannel,
  selectChannelById,
  selectAllChannels
} from 'src/features/account/channels.slice';
import useAuth from 'src/hooks/useAuth';
import { getPusherInstance } from 'src/services/auth.service';
import { getMessage } from 'src/features/account/messages.slice';
import IconButton from '@mui/material/IconButton';
import MarkUnreadChatAltIcon from '@mui/icons-material/MarkUnreadChatAlt';
import CloseIcon from '@mui/icons-material/Close';
import AuthService from 'src/services/auth.service';
import { fetchNotes } from 'src/features/notes/notes.slice';
import { debounce } from 'lodash';
import { selectNotificationsEnabled } from 'src/features/account/notifications.slice';
import ReplyIcon from '@mui/icons-material/Reply';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import MessageIcon from '@mui/icons-material/Message';
import PersonIcon from '@mui/icons-material/Person';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Avatar from '@mui/material/Avatar';
import store from 'src/store';
import { getListItem, getList } from 'src/services/boards';

const AccountNotifications = () => {
  const { isLoggedIn, authLoading } = useAuth();
  const { user } = useAppSelector(state => state.user);
  const contacts = useAppSelector(selectAllContacts);
  const notificationsEnabled = useAppSelector(selectNotificationsEnabled);
  const openConversationId = useAppSelector(state => state.conversations.openConversationId);
  const [onlineUsers, setOnlineUsers] = useState([]);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [pusherSubscriptionSucceeded, setPusherSubscriptionSucceeded] = useState(false);
  const [transcriptionDialogOpen, setTranscriptionDialogOpen] = useState(false);
  const [latestTranscriptionChunk, setLatestTranscriptionChunk] = useState(null);

  // Track recent requests to prevent duplicates
  const recentRequests = useRef(new Map());

  // Generic debounced function creator
  const createDebouncedDispatch = (action, keyPrefix, delay = 300) => {
    return debounce((id, payload = {}) => {
      const requestKey = `${keyPrefix}_${id}`;
      const now = Date.now();
      const lastRequest = recentRequests.current.get(requestKey);

      // Skip if we've made a request for this resource within the delay period
      if (lastRequest && now - lastRequest < delay) {
        return;
      }

      recentRequests.current.set(requestKey, now);
      
      if (typeof action === 'function') {
        dispatch(action(id, payload));
      } else {
        dispatch(action);
      }

      // Clean up old entries periodically
      if (recentRequests.current.size > 100) {
        const cutoff = now - 5000;
        for (const [key, timestamp] of recentRequests.current.entries()) {
          if (timestamp < cutoff) {
            recentRequests.current.delete(key);
          }
        }
      }
    }, delay);
  };

  // Debounced functions for API requests
  const debouncedGetContact = useRef(
    createDebouncedDispatch((contactId) => getContact(contactId), 'contact')
  ).current;

  const debouncedFetchConversation = useRef(
    createDebouncedDispatch(
      (conversationId, { messagesLimit = 0 }) => 
        fetchConversationWithMessages({ conversationId, messagesLimit }),
      'conversation'
    )
  ).current;

  const debouncedFetchChannel = useRef(
    createDebouncedDispatch((channelId) => fetchChannel(channelId), 'channel')
  ).current;

  const debouncedGetMessage = useRef(
    createDebouncedDispatch((messageId) => getMessage({ messageId }), 'message')
  ).current;

  const debouncedFetchNotes = useRef(
    createDebouncedDispatch(
      (key, { ownerType, ownerId }) => 
        fetchNotes({ ownerType, ownerId }),
      'notes',
      500
    )
  ).current;

  const debouncedFetchListItem = useRef(
    debounce(async (listId: string, itemId: string, boardId?: string) => {
      const requestKey = `list_item_${itemId}`;
      const now = Date.now();
      const lastRequest = recentRequests.current.get(requestKey);

      // Skip if we've made a request for this resource within 300ms
      if (lastRequest && now - lastRequest < 300) {
        return;
      }

      recentRequests.current.set(requestKey, now);
      
      try {
        const response = await getListItem(listId, itemId);
        // If we're in a board context, update the list item there
        // Otherwise, we might need to trigger a custom event or update a global store
        if (response.success && response.list_item) {
          // Dispatch a custom event that board components can listen to
          window.dispatchEvent(new CustomEvent('listItemUpdated', { 
            detail: { 
              listId, 
              itemId, 
              listItem: response.list_item,
              boardId // Include boardId in case we need to verify we're on the right board
            } 
          }));
        }
      } catch (error) {
        console.error('Error fetching list item:', error);
      }
    }, 300)
  ).current;


  // Enhanced snackbar action with rich UI/UX
  const snackbarAction = (key: string | number, data: {
    type: string;
    conversationId?: string;
    channelId?: string;
    contactId?: string;
    listItemId?: string;
  }) => {
    const { type, conversationId, channelId, contactId, listItemId } = data;

    const handleNavigation = () => {
      closeSnackbar(key);

      switch (type) {
        case 'message':
        case 'conversation':
          if (conversationId) {
            // Navigate to conversations page with the conversation open
            const conversation = selectConversationById(store.getState(), conversationId);
            if (conversation?.channel_id) {
              navigate(`/dashboard/conversations?conversation_id=${conversationId}&channel_id=${conversation.channel_id}`);
            } else {
              navigate(`/dashboard/conversations?conversation_id=${conversationId}`);
            }
            dispatch(toggleOpenConversation(conversationId));
          }
          break;
        case 'channel':
          if (channelId) {
            navigate(`/dashboard/channels/${channelId}`);
          }
          break;
        case 'contact':
          if (contactId) {
            navigate(`/dashboard/contacts/${contactId}`);
          }
          break;
        default:
          break;
      }
    };
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
        <IconButton 
          onClick={handleNavigation}
          size="small"
          sx={{ 
            color: 'white',
            p: 0.5,
            '&:hover': { 
              bgcolor: 'rgba(255, 255, 255, 0.2)'
            }
          }}
        >
          {type === 'message' || type === 'conversation' ? 
            <ReplyIcon fontSize="small" /> : 
            <OpenInNewIcon fontSize="small" />
          }
        </IconButton>
        <IconButton 
          onClick={() => closeSnackbar(key)}
          size="small"
          sx={{ 
            color: 'rgba(255, 255, 255, 0.8)',
            p: 0.25,
            '&:hover': { 
              color: 'white'
            }
          }}
        >
          <CloseIcon sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    );
  };

  // Enhanced notification display with rich content
  const showEnhancedNotification = (
    title: string, 
    body: string, 
    data: { 
      type: string; 
      conversationId?: string; 
      channelId?: string; 
      contactId?: string; 
      messageId?: string;
      listItemId?: string;
    }, 
    options: {
      variant?: 'info' | 'error' | 'success' | 'warning';
      icon?: React.ReactNode;
      priority?: boolean;
      soundType?: string;
    } = {}
  ) => {
    if (!notificationsEnabled) return;
    
    const { variant = 'info', icon, priority = false, soundType = 'default' } = options;
    
    // Don't show notification if we're already viewing the conversation
    if (data.type === 'message' && data.conversationId === openConversationId) {
      // Just play the sound if specified
      if (soundType && soundType !== 'none') {
        playNotificationSound(soundType);
      }
      return;
    }
    
    // Play sound based on type and priority
    playNotificationSound(soundType, priority);
    
    // Create compact notification content
    const notificationContent = (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        position: 'relative'
      }}>
        {priority && (
          <Box sx={{ 
            width: 3,
            height: '100%',
            position: 'absolute',
            left: -12,
            bgcolor: 'error.main',
            borderRadius: '2px'
          }} />
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600, 
                color: 'white',
                fontSize: '0.875rem',
                lineHeight: 1.2
              }}
            >
              {title}
            </Typography>
            {body && (
              <Typography 
                variant="body2" 
                sx={{ 
                  color: 'rgba(255, 255, 255, 0.9)', 
                  fontSize: '0.875rem',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  flex: 1
                }}
              >
                • {body}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    );
    
    enqueueSnackbar(notificationContent, {
      variant,
      hideIconVariant: true,
      autoHideDuration: priority ? 5000 : 3000,
      action: key => snackbarAction(key, data),
      anchorOrigin: { vertical: 'top', horizontal: 'right' }
    });
  };
  
  // Play notification sounds based on type and priority
  const playNotificationSound = (type: string, isPriority: boolean = false) => {
    if (!notificationsEnabled) return;
    
    let audioFile = '/audio/message_received.mp3';
    
    switch (type) {
      case 'alert':
      case 'needs_attention':
        audioFile = '/audio/alert.mp3';
        break;
      case 'function':
      case 'keyboard':
        audioFile = '/audio/keyboard.mp3';
        break;
      case 'sent':
        audioFile = '/audio/message_sent.mp3';
        break;
      case 'connection':
        audioFile = '/audio/dialup.mp3';
        break;
      case 'none':
        return;
      default:
        audioFile = isPriority ? '/audio/alert.mp3' : '/audio/message_received.mp3';
    }
    
    try {
      const audio = new Audio(audioFile);
      audio.play().catch(e => console.log('Audio play failed:', e));
    } catch (e) {
      console.error('Error playing sound:', e);
    }
  };

  // for a new onlineUser add a enqueueSnackbar
  const newOnlineUserSnackbar = (onlineUser) => {
    if (!notificationsEnabled) return;

    enqueueSnackbar(`${onlineUser.name} is online`, {
      variant: 'success',
      anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
    });
  }

  useEffect(() => {
    if (notificationsEnabled && onlineUsers.length > 0) {
      newOnlineUserSnackbar(onlineUsers[0]);
    }
  }, [onlineUsers, notificationsEnabled]);


  useEffect(() => {
    if (isLoggedIn && user) {
      // Initialize Pusher and ensure we're signed in
      AuthService.pusherSignin();

      // Get the Pusher instance
      const pusherInstance = getPusherInstance();

      if (pusherInstance && user.account?.uuid) {
        // Subscribe to presence channel for the account
        const channel = pusherInstance.subscribe('presence-account-' + user.account.uuid);

        // Handle subscription success
        channel.bind('pusher:subscription_succeeded', () => {
          console.log('Pusher subscription succeeded for channel:', 'presence-account-' + user.account.uuid);
          setPusherSubscriptionSucceeded(true);
        });

        // Handle subscription error
        channel.bind('pusher:subscription_error', (error) => {
          console.error('Presence channel subscription error:', error);
          setPusherSubscriptionSucceeded(false);
          if (notificationsEnabled) {
            enqueueSnackbar('Check your internet connection', {
              variant: 'error',
              anchorOrigin: { vertical: 'bottom', horizontal: 'left' }
            });
          }
        });

        // Bind all the event handlers
        console.log('Binding event handlers for Pusher channel');
        bindEvents(channel);

        // Cleanup function
        return () => {
          try {
            channel.unbind_all();
            pusherInstance.unsubscribe('presence-account-' + user.account.uuid);
          } catch (error) {
            console.error('Error cleaning up Pusher channel:', error);
          }
        };
      }
    }
  }, [isLoggedIn, user]);

  // Separate bindEvents into its own function for clarity
  const bindEvents = (channel) => {
    // Channel events with enhanced notifications
    ['channel_created', 'channel_updated', 'channel_deleted'].forEach(eventName => {
      channel.bind(eventName, (data) => {
        if (eventName === 'channel_created' || eventName === 'channel_updated') {
          debouncedFetchChannel(data.channel.id);
          
          // Show notification for channel events only if notifications enabled
          if (notificationsEnabled && data.channel?.notification?.notification) {
            const { title, body } = data.channel.notification.notification;
            showEnhancedNotification(
              title || `Channel ${eventName.replace('_', ' ')}`,
              body,
              { type: 'channel', channelId: data.channel.id },
              { icon: <MessageIcon />, variant: 'info' }
            );
          }
        }
      });
    });

    // Conversation events with status-aware notifications
    ['conversation_created', 'conversation_updated', 'conversation_deleted'].forEach(eventName => {
      channel.bind(eventName, (data) => {
        if (eventName === 'conversation_created' || eventName === 'conversation_updated') {
          // Check if the conversation is in a loading state to avoid race conditions
          const existingConversation = selectConversationById(store.getState(), data.conversation.id);
          if (!existingConversation?.loading) {
            // Fetch with at least 1 message for conversation_created so we can show preview
            const messagesLimit = eventName === 'conversation_created' ? 3 : 0;
            debouncedFetchConversation(data.conversation.id, { messagesLimit });
          }
          
          // Only show notifications if enabled
          if (notificationsEnabled) {
            // Check if conversation needs attention
            const isNeedsAttention = data.conversation?.status === 'needs_attention';
            const isPriority = data.conversation?.metadata?.priority === true;

            // For conversation_updated, only show notification if it's needs_attention
            if (eventName === 'conversation_updated' && !isNeedsAttention) {
              return;
            }
            
            // Show enhanced notification for conversation events (except updates)
            if (data.conversation?.notification?.notification || isNeedsAttention || eventName === 'conversation_created') {
              const notification = data.conversation?.notification?.notification || {};

              // For conversation_created, try to get the first message content
              let messagePreview = '';
              if (eventName === 'conversation_created') {
                // Check if conversation data includes messages
                if (data.conversation?.messages && data.conversation.messages.length > 0) {
                  const firstMessage = data.conversation.messages[0];
                  if (firstMessage.content) {
                    messagePreview = firstMessage.content.length > 60 ?
                      firstMessage.content.substring(0, 60).trim() + '...' :
                      firstMessage.content;
                  }
                } else if (data.conversation?.subject) {
                  // Use subject as fallback
                  messagePreview = data.conversation.subject.length > 60 ?
                    data.conversation.subject.substring(0, 60).trim() + '...' :
                    data.conversation.subject;
                }
              }

              const title = notification.title ||
                (isNeedsAttention ? '⚠️ Conversation Needs Attention' :
                 eventName === 'conversation_created' ? 'New Conversation' :
                 'Conversation updated');

              const body = notification.body || messagePreview ||
                (isNeedsAttention ? 'This conversation requires your immediate attention' :
                 eventName === 'conversation_created' && !messagePreview ? 'Click to view conversation' : '');

              showEnhancedNotification(
                title,
                body,
                {
                  type: 'conversation',
                  conversationId: data.conversation.id,
                  channelId: data.conversation.channel_id
                },
                {
                  icon: <MessageIcon />,
                  variant: isNeedsAttention ? 'error' : 'info',
                  priority: isNeedsAttention || isPriority,
                  soundType: isNeedsAttention ? 'alert' : 'default'
                }
              );
            }
          }
        }
        if (eventName === 'conversation_deleted') {
          dispatch(removeConversationById(data.conversation.id));
        }
      });
    });

    // Message events with enhanced notifications
    ['message_created', 'message_updated', 'message_deleted'].forEach(eventName => {
      channel.bind(eventName, (data) => {
        if (eventName === 'message_created' || eventName === 'message_updated') {
          debouncedGetMessage(data.message.id);
          
          if (notificationsEnabled && eventName === 'message_created') {
            const senderRole = data?.message?.role;
            
            // Skip function messages entirely
            if (senderRole === 'function') {
              return;
            }
            
            // Skip if no actual content
            const messageContent = data?.message?.content || '';
            const hasAttachments = data?.message?.metadata?.has_attachments;
            const messageType = data?.message?.metadata?.type || 'text';
            
            // Skip if no meaningful content to show
            if (!messageContent.trim() && !hasAttachments && messageType === 'text') {
              return;
            }
            
            // Get conversation to check status
            const conversationId = data.message.conversation_id;
            const conversation = conversationId ? 
              selectConversationById(store.getState(), conversationId) : null;
            
            const isNeedsAttention = conversation?.status === 'needs_attention';
            
            // Get sender info from conversation participants
            const senderEntity = conversation?.participating_entities?.find(
              entity => entity.id === data.message.sender_id
            );
            
            // Clean sender identification
            let senderName = '';
            
            if (senderRole === 'assistant') {
              // Get the channel name for better context
              const channel = conversation?.channel_id ? 
                selectChannelById(store.getState(), conversation.channel_id) : null;
              senderName = channel?.chatbot_name || senderEntity?.name || 'Assistant';
            } else {
              // Get user's actual name
              const fullName = senderEntity?.name || '';
              if (fullName) {
                senderName = fullName;
              } else if (senderEntity?.identifier) {
                // Extract from email if no name
                const emailName = senderEntity.identifier.split('@')[0];
                senderName = emailName.charAt(0).toUpperCase() + emailName.slice(1).replace(/[._-]/g, ' ');
              } else {
                senderName = 'Customer';
              }
            }
            
            // Clean message preview
            let messagePreview = '';
            
            if (messageType === 'voice') {
              messagePreview = 'Voice message';
            } else if (messageType === 'video') {
              messagePreview = 'Video message';
            } else if (hasAttachments) {
              const attachmentCount = data?.message?.metadata?.attachment_count || 1;
              messagePreview = attachmentCount > 1 ? 
                `${attachmentCount} files` : 
                'File attached';
            } else if (messageContent.includes('http://') || messageContent.includes('https://')) {
              messagePreview = 'Shared a link';
            } else if (messageContent.length > 60) {
              messagePreview = messageContent.substring(0, 60).trim() + '...';
            } else {
              messagePreview = messageContent;
            }
            
            // Clean title
            const enhancedTitle = senderName;
            
            // Show compact notification for messages
            showEnhancedNotification(
              enhancedTitle,
              messagePreview,
              { 
                type: 'message', 
                conversationId,
                messageId: data.message.id 
              },
              { 
                variant: isNeedsAttention ? 'error' : 'info',
                priority: isNeedsAttention,
                soundType: isNeedsAttention ? 'alert' : 'default'
              }
            );
          }
        }

        if (eventName === 'message_deleted') {
          dispatch(removeMessageFromConversation({
            conversationId: data.message.conversation_id,
            messageId: data.message.id
          }));
        }
      });
    });

    ['transcript_updated'].forEach(eventName => {
      channel.bind(eventName, (data) => {
        setLatestTranscriptionChunk(data.transcription); // Pass the new chunk
        if (notificationsEnabled && !transcriptionDialogOpen) {
          setTranscriptionDialogOpen(true);
        }
      });
    });

    // Contact events with enhanced notifications
    ['contact_created', 'contact_updated', 'contact_deleted'].forEach(eventName => {
      channel.bind(eventName, (data) => {
        if (data.contact && data.contact.id) {
          console.log('contact event', eventName);
          if (eventName === 'contact_created') {
            debouncedGetContact(data.contact.id);
            // Don't show notification for contact_created as requested
          } else if (eventName === 'contact_updated') {
            // Only update contact if it already exists in the store
            const contactExists = contacts.some(contact => contact.id === data.contact.id);
            if (contactExists) {
              debouncedGetContact(data.contact.id);
            }
          } else if (eventName === 'contact_deleted') {
            dispatch(removeContactState(data.contact.id));
            // No notification for contact_deleted as requested
          }
        }
      });
    });

    ['entity_created', 'entity_updated', 'entity_deleted'].forEach(eventName => {
      channel.bind(eventName, (data) => {
        // Only handle if notifications enabled
        // setLatestChannelPusherEvent({ eventName, data });
      });
    });

    ['faq_created', 'faq_updated', 'faq_deleted'].forEach(eventName => {
      channel.bind(eventName, (data) => {
        // Only handle if notifications enabled
        // setLatestChannelPusherEvent({ eventName, data });
      });
    });

    // Add note events handling
    ['note_created', 'note_updated', 'note_deleted'].forEach(eventName => {
      channel.bind(eventName, (data) => {
        if (data?.note) {
          // Fetch updated notes for the owner
          // dispatch(fetchNotes({
          //   ownerType: data.note.owner_type,
          //   ownerId: data.note.owner_id
          // }));

          // Show notification if present (only if notifications are enabled)
          // if (notificationsEnabled && data.note?.notification?.notification) {
          //   const { title, body } = data.note.notification.notification;
          //   enqueueSnackbar(`${title}: ${body}`, {
          //     variant: 'info',
          //     autoHideDuration: 3000,
          //   });
          // }
        }
      });
    });

    // List events (and board events)
    ['list_created', 'list_updated', 'list_deleted', 'board_created', 'board_updated', 'board_deleted'].forEach(eventName => {
      console.log(`Binding to event: ${eventName}`);
      channel.bind(eventName, (data) => {
        console.log(`Received ${eventName} event:`, data);
        if (data?.list || data?.board) {
          console.log('list/board event data:', data.list || data.board);
          
          if (eventName === 'list_created' || eventName === 'list_updated') {
            // Dispatch event for list updates
            window.dispatchEvent(new CustomEvent('listUpdated', {
              detail: {
                listId: data.list.id,
                list: data.list,
                boardId: data.list.board_id,
                eventType: eventName
              }
            }));
          } else if (eventName === 'list_deleted') {
            window.dispatchEvent(new CustomEvent('listDeleted', {
              detail: {
                listId: data.list.id,
                boardId: data.list.board_id
              }
            }));
          }
        }
      });
    });

    // List item events (note: backend sends 'listitem' not 'list_item')
    ['listitem_created', 'listitem_updated', 'listitem_deleted'].forEach(eventName => {
      console.log(`Binding to event: ${eventName}`);
      channel.bind(eventName, (data) => {
        console.log(`Received ${eventName} event:`, data);
        if (data?.listitem) {  // Changed from list_item to listitem
          console.log('list item event data:', data.listitem);
          
          // For created and updated events, fetch the updated list item
          if (eventName === 'listitem_created' || eventName === 'listitem_updated') {
            // We need the list_id to fetch the item
            if (data.listitem.list_id && data.listitem.id) {
              // Try to get boardId from the listitem data if available
              const boardId = data.listitem.board_id || data.listitem.metadata?.board_id;
              debouncedFetchListItem(data.listitem.list_id, data.listitem.id, boardId);
            }
            
            // Show notification if enabled
            if (notificationsEnabled && data.listitem?.notification?.notification) {
              const { title, body } = data.listitem.notification.notification;
              showEnhancedNotification(
                title || `List item ${eventName.replace('listitem_', '').replace('_', ' ')}`,
                body,
                { type: 'list_item', listItemId: data.listitem.id },
                { variant: 'info' }
              );
            }
          } else if (eventName === 'listitem_deleted') {
            // For deleted events, dispatch a custom event
            window.dispatchEvent(new CustomEvent('listItemDeleted', { 
              detail: { 
                listId: data.listitem.list_id, 
                itemId: data.listitem.id,
                boardId: data.listitem.board_id
              } 
            }));
          }
        }
      });
    });
  };

  return null;
};

export default AccountNotifications;
